import React from "react";
import BfFileDownload from "../../components/BfFileDownload";

// NSwag AngularJS FileResponse (common shape)
type FileResponse = {
  data: Blob;
  status: number;
  fileName?: string | null;
  headers?: Record<string, string> | (() => Record<string, string>);
};

type AdminDataDashboardClient = {
  exportDealCompletionXls: () => ng.IPromise<FileResponse>;
  exportLenderUsersXls: () => ng.IPromise<FileResponse>;
  exportMissingProductSnapshotEligibilityXls: () => ng.IPromise<FileResponse>;
};

type Props = {
  AdminDataDashboardClient: AdminDataDashboardClient;
  $rootScope?: any;
};

type ReportId = "lender-users" | "missing-product-snapshot-eligibility" | "broker-completions";
type Report = { id: ReportId; label: string; filename?: string; icon?: string };
type Section = { title: string; reports: Report[] };

function toDownloadResponse(fr: FileResponse) {
  const getHeaders = () => (typeof fr.headers === "function" ? fr.headers() : fr.headers ?? {});
  return fr.data.arrayBuffer().then((ab) => ({ data: ab, headers: getHeaders }));
}

const AdminDataDashboard: React.FC<Props> = ({ AdminDataDashboardClient: api, $rootScope }) => {
  const lenderRelations: Section = {
    title: "Lender Relations",
    reports: [
      { id: "lender-users", label: "Lender Users (XLSX)", filename: "lender-users.xlsx" },
      {
        id: "missing-product-snapshot-eligibility",
        label: "Missing product snapshots and eligibility (XLSX)",
        filename: "missing-product-snapshot-eligibility.xlsx",
      },
    ],
  };

  const customerSuccess: Section = {
    title: "Customer Success",
    reports: [
      {
        id: "broker-completions",
        label: "Broker completions (XLSX)",
        filename: "broker-completions.xlsx",
      },
    ],
  };

  const makeRequest = React.useCallback(
    (id: ReportId) => {
      return async () => {
        let fr: FileResponse;
        switch (id) {
          case "lender-users":
            fr = await api.exportLenderUsersXls();
            break;
          case "missing-product-snapshot-eligibility":
            fr = await api.exportMissingProductSnapshotEligibilityXls();
            break;
          case "broker-completions":
            fr = await api.exportDealCompletionXls();
            break;
          default:
            throw new Error(`Unknown report id: ${id}`);
        }
        return toDownloadResponse(fr);
      };
    },
    [api],
  );

  const SectionBlock: React.FC<{ section: Section }> = ({ section }) => (
    <section className="ard-section">
      <h2 className="ard-section__title">{section.title}</h2>
      <div className="ard-section__list">
        {section.reports.map((r) => (
          <div key={r.id} className="ard-card">
            <div className="ard-card__meta">
              <div className="ard-card__label">{r.label}</div>
            </div>
            <div className="ard-card__actions">
              <BfFileDownload
                request={makeRequest(r.id)}
                filename={r.filename} // fallback if server doesn’t send one
                label="Download"
                icon={r.icon}
                $rootScope={$rootScope}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );

  return (
    <div className="ard-root">
      <header className="ard-header">
        <div>
          <h1 className="ard-header__title">Admin Reports</h1>
          <p className="ard-header__subtitle">Download internal MI extracts</p>
        </div>
      </header>

      <div className="ard-grid">
        <SectionBlock section={lenderRelations} />
        <SectionBlock section={customerSuccess} />
      </div>
    </div>
  );
};

export default AdminDataDashboard;
